// src/components/auth/VerificationStep.tsx
// Verify the user's mobile number (SMS) or email with a 6-digit code.
// Mount it wherever the onboarding / Settings flow needs it, AFTER the mobile
// number has been saved via PATCH /auth/me (the server texts the SAVED number,
// never one sent from the browser).
import { useCallback, useEffect, useState } from 'react';
import { OTPInput } from '@/components/ui/OTPInput';
import {
  classifyVerificationError,
  confirmVerificationCode,
  requestVerificationCode,
  type VerificationChannel,
} from '@/lib/services/verification';
import { codeLength } from '@/lib/utils/codeLength';

type Props = {
  initialChannel?: VerificationChannel;
  /** From GET /auth/me. SMS is only offered when a number is saved. */
  hasCellNumber: boolean;
  onVerified: (channel: VerificationChannel) => void;
  /** Offer "Use SMS/email instead". Turn off when only one channel makes sense (e.g. verifying the saved mobile number). */
  allowSwitch?: boolean;
};

export function VerificationStep({
  initialChannel = 'email',
  hasCellNumber,
  onVerified,
  allowSwitch = true,
}: Props) {
  const [channel, setChannel] = useState<VerificationChannel>(
    initialChannel === 'sms' && !hasCellNumber ? 'email' : initialChannel,
  );
  const [code, setCode] = useState('');
  const [inputKey, setInputKey] = useState(0); // remount OTPInput to clear it
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = useCallback(async (ch: VerificationChannel) => {
    setBusy(true);
    setError(null);
    setCode('');
    setInputKey((k) => k + 1);
    try {
      const r = await requestVerificationCode(ch);
      setSentTo(r.sent_to);
      setCooldown(r.resend_after_seconds);
    } catch (err) {
      const { kind, message } = classifyVerificationError(err);
      setSentTo(null);
      if (kind === 'cooldown') setCooldown(60);
      // 503 on SMS already says "...or use email instead"; the switch link below acts on it.
      setError(message);
    } finally {
      setBusy(false);
    }
  }, []);

  // Send on mount and whenever the user switches channel.
  useEffect(() => {
    void send(channel);
  }, [channel, send]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  async function verify() {
    setBusy(true);
    setError(null);
    try {
      await confirmVerificationCode(channel, code);
      onVerified(channel);
    } catch (err) {
      setError(classifyVerificationError(err).message);
      setCode('');
      setInputKey((k) => k + 1);
    } finally {
      setBusy(false);
    }
  }

  const other: VerificationChannel = channel === 'sms' ? 'email' : 'sms';
  const canSwitch = allowSwitch && (other === 'email' || hasCellNumber);
  const ready = code.length === codeLength(channel) && !busy;

  return (
    <section style={{ width: '100%', maxWidth: 420 }}>
      <p
        className="text-center"
        style={{ color: 'rgba(var(--fg-rgb),0.5)', fontSize: 13, lineHeight: 1.5, marginBottom: 24 }}
      >
        {sentTo
          ? `We sent a ${codeLength(channel)}-digit code to ${sentTo} by ${channel === 'sms' ? 'SMS' : 'email'}.`
          : 'Sending your code…'}
      </p>

      <OTPInput key={`${inputKey}-${channel}`} length={codeLength(channel)} onChange={setCode} onComplete={setCode} />

      <button
        type="button"
        onClick={verify}
        disabled={!ready}
        className="btn-pill"
        style={{
          width: '100%',
          marginTop: 28,
          opacity: ready ? 1 : 0.4,
          cursor: ready ? 'pointer' : 'not-allowed',
        }}
      >
        {busy ? 'Working…' : 'Verify'}
      </button>

      {error && (
        <p className="mono-label" role="alert" style={{ marginTop: 10, color: 'var(--color-error)', textAlign: 'center' }}>
          {error}
        </p>
      )}

      <div className="flex items-center justify-center gap-6 mt-4">
        <button
          type="button"
          className="mono-label"
          onClick={() => void send(channel)}
          disabled={busy || cooldown > 0}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'rgba(var(--fg-rgb),0.45)',
            cursor: busy || cooldown > 0 ? 'not-allowed' : 'pointer',
          }}
        >
          {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend Code'}
        </button>
        {canSwitch && (
          <button
            type="button"
            className="mono-label"
            onClick={() => setChannel(other)}
            disabled={busy}
            style={{ background: 'transparent', border: 'none', color: 'rgba(var(--fg-rgb),0.45)', cursor: 'pointer' }}
          >
            Use {other === 'sms' ? 'SMS' : 'email'} instead
          </button>
        )}
      </div>
    </section>
  );
}
